<template>
    <div class="pnl-pivot">
        <div class="pnl-pivot__toolbar">
            <b-form-select
                :value="year"
                :options="yearOptions"
                size="sm"
                style="width: 110px;"
                class="mr-3"
                @change="$emit('year-change', $event)"
            />
            <span class="pnl-pivot__label">집계 단위</span>
            <b-button-group size="sm" class="mr-3">
                <b-button
                    v-for="u in unitOptions"
                    :key="u.value"
                    :variant="unit === u.value ? 'primary' : 'outline-secondary'"
                    @click="unit = u.value"
                >{{ u.text }}</b-button>
            </b-button-group>
            <small class="text-muted">▸ 를 눌러 접고 펼침 · 셀을 클릭하면 그 기간으로 조회합니다</small>
            <div class="flex-grow-1"></div>
            <b-spinner v-if="loading" small variant="primary" class="mr-2" />
            <b-button size="sm" variant="outline-primary" @click="$emit('open-costs')">
                <i class="fas fa-plus mr-1"></i>비용 입력
            </b-button>
        </div>

        <div class="pnl-pivot__scroll">
            <table class="pnl-pivot__table">
                <thead>
                    <tr>
                        <th class="acct">계정</th>
                        <th v-for="c in columns" :key="c.key">{{ c.label }}</th>
                        <th class="col-total">합계</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(row, i) in rows" :key="i" :class="row.cls">
                        <th class="acct">
                            <span
                                v-if="row.toggle"
                                class="toggle"
                                :class="{ open: openGroups[row.toggle] }"
                                @click="toggleGroup(row.toggle)"
                            >{{ row.label }}</span>
                            <span v-else>{{ row.label }}</span>
                            <small v-if="row.userCat" class="user-cat">사용자 분류</small>
                        </th>
                        <td
                            v-for="(c, ci) in columns"
                            :key="c.key"
                            :class="cellClass(row, ci)"
                            @click="onCellClick(row, c)"
                        >{{ cellText(row, ci) }}</td>
                        <td class="col-total" :class="{ neg: totalValue(row) < 0 }">{{ fmtRow(row, totalValue(row)) }}</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="!columns.length" class="text-center text-muted py-4">
            조회된 기간에 데이터가 없습니다.
        </div>
    </div>
</template>

<script>
/**
 * 손익 피벗 테이블 — 행=계정과목, 열=기간(연/반기/분기/월)
 *
 * 고정비는 상품에 배분하지 않는다. 총이익(GP) 아래 기간 라인으로만 붙는다.
 * 부가세는 쿠팡 판매분 기준 공식을 그대로 두고, 비용 중 '부가세 포함(vat)'으로
 * 등록한 것만 금액÷11 을 '부가세 환급분'으로 되돌린다.
 * 자세한 근거는 docs/sales-ledger-pnl-design.md
 */
export default {
    name: 'PnlPivotTable',
    props: {
        // 서버 월별 집계 (getLedgerMonthlySummary). 조회 기간과 무관하게 그 해 전체가 들어온다
        monthlySummary: { type: Array, default: () => [] },
        // 월 스냅샷 고정비 [{ month:'YYYY-MM', items:[{name,category,amount,tax}] }]
        fixedCosts: { type: Array, default: () => [] },
        // 상품 직접비 [{ date, vendorItemId, category, amount, tax }]
        productCosts: { type: Array, default: () => [] },
        // 연도 선택
        year: { type: [String, Number], default: '' },
        years: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false }
    },
    data() {
        return {
            unit: 'month',
            unitOptions: [
                { value: 'year', text: '연' },
                { value: 'half', text: '반기' },
                { value: 'quarter', text: '분기' },
                { value: 'month', text: '월' }
            ],
            // 판매비용/매입·부가세는 접어두고, 새로 생긴 항목은 펼쳐서 보여준다
            openGroups: { sc: false, mkt: true, gp: false, fx: true }
        };
    },
    computed: {
        yearOptions() {
            const list = (this.years || []).map(y => ({ value: String(y), text: y + '년' }));
            if (!list.length && this.year) list.push({ value: String(this.year), text: this.year + '년' });
            return list;
        },

        // 기간 키 목록 — 데이터가 있는 구간만. 회사마다 시작 시점이 다르므로 하드코딩하지 않는다
        columns() {
            const keys = new Set();
            this.monthlySummary.forEach(m => {
                const k = this.periodKeyOf(m.month);
                if (k) keys.add(k);
            });
            (this.fixedCosts || []).forEach(f => {
                const k = this.periodKeyOf(f.month);
                if (k) keys.add(k);
            });
            return Array.from(keys).sort().map(key => ({ key, label: this.periodLabel(key) }));
        },

        // 기간별 집계값
        periodData() {
            const map = {};
            const ensure = key => {
                if (!map[key]) {
                    map[key] = {
                        sales: 0, instant: 0, download: 0, netSales: 0,
                        commission: 0, shipping: 0, warehouse: 0, storage: 0,
                        creturn: 0, vreturn: 0, vreturnShipping: 0, barcode: 0,
                        milkrun: 0, compensation: 0, ad: 0,
                        sellingCosts: 0, contribCp: 0, cogs: 0, vat: 0, gpServer: 0, excluded: 0,
                        extMkt: 0, fixedTotal: 0,
                        // 기타비용 공제는 부가세 행에 바로 반영하고(일별·상품별 표와 동일),
                        // 고정비 공제만 '부가세 환급분' 행으로 따로 보여준다
                        mktRefund: 0, fixedRefund: 0,
                        mktByCat: {}, fixedByCat: {}
                    };
                }
                return map[key];
            };

            // 서버가 월별로 미리 집계해 준 값 (계산식은 calculateProductMetricsV3와 동일).
            // 판매비용합계·기여이익·부가세·총이익에는 기타비용(상품 직접비)이 이미 반영돼 있다.
            // 여기서 또 빼면 이중 차감이 되므로 서버 값을 그대로 쓴다
            this.monthlySummary.forEach(d => {
                const key = this.periodKeyOf(d.month);
                if (!key) return;
                const p = ensure(key);
                p.sales += this.num(d.saleAmount);
                p.instant += this.num(d.instantCoupon);
                p.download += this.num(d.downloadCoupon);
                p.netSales += this.num(d.netSales);
                p.commission += this.num(d.commissionTotal);
                p.shipping += this.num(d.shippingTotal);
                p.warehouse += this.num(d.warehouseTotal);
                p.storage += this.num(d.storageFee);
                p.creturn += this.num(d.creturnFee);
                p.vreturn += this.num(d.vreturnFee);
                p.vreturnShipping += this.num(d.vreturnShippingFee);
                p.barcode += this.num(d.barcodeFee);
                p.milkrun += this.num(d.milkrunCost);
                p.compensation += this.num(d.inventoryCompensation);
                p.ad += this.num(d.adTotal);
                // 표의 '판매비용합계' 행은 아래 상세 행(수수료·배송비…광고비)의 합이어야 한다.
                // 서버 값에는 기타비용이 들어 있으므로 그만큼 덜어내 화면 구성을 유지한다
                p.sellingCosts += this.num(d.sellingCosts) - this.num(d.externalCost);
                p.contribCp += this.num(d.contribution);
                p.cogs += this.num(d.cogs);
                p.vat += this.num(d.vat);
                p.gpServer += this.num(d.grossProfit);
                p.excluded += this.num(d.excludedContribution);
                p.extMkt += this.num(d.externalCost);
                p.mktRefund += this.num(d.externalVatRefund);
            });

            // 상품 직접비 — 합계는 서버가 준 값을 쓰고, 여기서는 분류별로만 쪼갠다
            // (분류는 사용자가 만든 문자열이라 서버 집계에 없다)
            (this.productCosts || []).forEach(c => {
                const key = this.periodKeyOf(c.date);
                if (!key) return;
                const p = ensure(key);
                const cat = (c.category || '').trim() || '분류없음';
                p.mktByCat[cat] = (p.mktByCat[cat] || 0) + this.num(c.amount);
            });

            // 기간 고정비
            (this.fixedCosts || []).forEach(f => {
                const key = this.periodKeyOf(f.month + '-01');
                if (!key) return;
                const p = ensure(key);
                (f.items || []).forEach(it => {
                    const amt = this.num(it.amount);
                    const cat = (it.category || '').trim() || '분류없음';
                    p.fixedTotal += amt;
                    p.fixedByCat[cat] = (p.fixedByCat[cat] || 0) + amt;
                    if (it.tax === 'vat') p.fixedRefund += Math.round(amt / 11);
                });
            });

            // 파생값
            Object.keys(map).forEach(key => {
                const p = map[key];
                // 기여이익·부가세·총이익은 서버가 기타비용까지 반영해 계산한 값이다.
                // 여기서 다시 빼면 이중 차감이 된다.
                // 총이익의 차액(p.excluded)은 '매입가 미입력' 줄로 따로 보여주므로 표는 맞아떨어진다
                p.contrib = p.contribCp;
                p.vatNet = p.vat;
                p.gp = p.gpServer;
                p.opProfit = p.gp - p.fixedTotal + p.fixedRefund;
                p.mktTotal = p.ad + p.extMkt;
            });
            return map;
        },

        // 매입가를 몰라 빠진 금액이 있을 때만 그 줄을 보여준다
        hasExcluded() {
            return Object.keys(this.periodData).some(k => Math.abs(this.periodData[k].excluded) > 0);
        },

        marketingCategories() {
            return this.collectCategories('mktByCat');
        },
        fixedCategories() {
            return this.collectCategories('fixedByCat');
        },

        rows() {
            const r = [];
            const push = (label, field, cls, opt) => r.push({ label, field, cls: cls || '', ...(opt || {}) });

            push('총판매액', 'sales', 'lv0');
            push('즉시할인쿠폰', 'instant', 'detail', { neg: true });
            push('다운로드쿠폰', 'download', 'detail', { neg: true });
            push('순매출액', 'netSales', 'lv0 sep');

            push('판매비용합계', 'sellingCosts', 'sum', { toggle: 'sc', neg: true });
            if (this.openGroups.sc) {
                [['수수료', 'commission'], ['배송비', 'shipping'], ['입출고비', 'warehouse'],
                 ['보관비', 'storage'], ['반품회수재입고비', 'creturn'], ['반출비', 'vreturn'],
                 ['반출배송비', 'vreturnShipping'], ['바코드비', 'barcode'], ['밀크런비용', 'milkrun'],
                 ['쿠팡광고비', 'ad']].forEach(([l, f]) => push(l, f, 'detail', { neg: true }));
                push('재고손실보상', 'compensation', 'detail');
            }

            push('기타비용', 'extMkt', 'sum', { toggle: 'mkt', neg: true, isNew: true });
            if (this.openGroups.mkt) {
                this.marketingCategories.forEach(cat =>
                    push(cat, 'mktByCat.' + cat, 'detail', { neg: true, userCat: true }));
            }

            push('기여이익', 'contrib', 'lv0 sep');
            push('매입원가 · 부가세', '__group', 'sum', { toggle: 'gp' });
            if (this.openGroups.gp) {
                push('매입원가', 'cogs', 'detail', { neg: true });
                push('부가세', 'vatNet', 'detail', { neg: true });
            }
            // 매입가를 몰라 이익 계산에서 뺀 금액. 이 줄이 있어야 위 행을 빼면 총이익이 맞는다
            if (this.hasExcluded) {
                push('매입가 미입력', 'excluded', 'detail warn', { neg: true });
            }
            push('총이익(GP)', 'gp', 'lv0');

            push('고정비합계', 'fixedTotal', 'sum sep', { toggle: 'fx', neg: true, isNew: true });
            if (this.openGroups.fx) {
                this.fixedCategories.forEach(cat =>
                    push(cat, 'fixedByCat.' + cat, 'detail', { neg: true, userCat: true }));
                push('부가세 환급분', 'fixedRefund', 'detail', { isNew: true });
            }
            push('영업이익', 'opProfit', 'op', { isNew: true });

            push('영업이익률', '__rate_op', 'rate');
            push('BEP매출', '__bep', 'rate');
            push('총마케팅 대비 매출', '__mkt_ratio', 'rate');
            return r;
        }
    },
    methods: {
        num(v) {
            const n = Number(v);
            return isNaN(n) ? 0 : n;
        },
        // 'YYYY-MM-DD' → 기간 키
        periodKeyOf(date) {
            if (!date || typeof date !== 'string' || date.length < 7) return '';
            const y = date.slice(0, 4);
            const m = parseInt(date.slice(5, 7), 10);
            if (!y || !m) return '';
            if (this.unit === 'year') return y;
            if (this.unit === 'half') return y + '-H' + (m <= 6 ? 1 : 2);
            if (this.unit === 'quarter') return y + '-Q' + Math.ceil(m / 3);
            return date.slice(0, 7);
        },
        periodLabel(key) {
            if (this.unit === 'year') return key + '년';
            if (this.unit === 'half') return key.slice(0, 4) + '년 ' + (key.endsWith('H1') ? '상반기' : '하반기');
            if (this.unit === 'quarter') return key.slice(0, 4) + '년 ' + key.slice(6) + '분기';
            return parseInt(key.slice(5, 7), 10) + '월';
        },
        collectCategories(bucket) {
            const set = [];
            Object.keys(this.periodData).forEach(k => {
                Object.keys(this.periodData[k][bucket] || {}).forEach(cat => {
                    if (!set.includes(cat)) set.push(cat);
                });
            });
            return set.sort();
        },
        // 행 × 기간 → 값
        valueOf(row, periodKey) {
            const p = this.periodData[periodKey];
            if (!p) return null;
            const f = row.field;
            if (f === '__group') return null;
            if (f === '__rate_op') return p.netSales ? p.opProfit / p.netSales : 0;
            if (f === '__bep') {
                const cr = p.netSales ? p.contrib / p.netSales : 0;
                return cr > 0 ? Math.round((p.fixedTotal - p.fixedRefund) / cr) : 0;
            }
            if (f === '__mkt_ratio') return p.mktTotal ? p.netSales / p.mktTotal : 0;
            if (f.indexOf('.') > -1) {
                const [bucket, cat] = f.split(/\.(.+)/);
                return (p[bucket] || {})[cat] || 0;
            }
            return p[f] || 0;
        },
        totalValue(row) {
            const f = row.field;
            if (f === '__group') return null;
            // 비율 행은 합계 기간을 하나로 합쳐 다시 계산해야 맞다
            const sum = this.columns.reduce((acc, c) => {
                const p = this.periodData[c.key];
                if (!p) return acc;
                acc.netSales += p.netSales;
                acc.opProfit += p.opProfit;
                acc.contrib += p.contrib;
                acc.fixedTotal += p.fixedTotal;
                acc.fixedRefund += p.fixedRefund;
                acc.mktTotal += p.mktTotal;
                acc.plain += this.valueOf(row, c.key) || 0;
                return acc;
            }, { netSales: 0, opProfit: 0, contrib: 0, fixedTotal: 0, fixedRefund: 0, mktTotal: 0, plain: 0 });

            if (f === '__rate_op') return sum.netSales ? sum.opProfit / sum.netSales : 0;
            if (f === '__bep') {
                const cr = sum.netSales ? sum.contrib / sum.netSales : 0;
                return cr > 0 ? Math.round((sum.fixedTotal - sum.fixedRefund) / cr) : 0;
            }
            if (f === '__mkt_ratio') return sum.mktTotal ? sum.netSales / sum.mktTotal : 0;
            return sum.plain;
        },
        // 화면 표시값 — 비용 계정은 음수로 보여준다
        displayValue(row, periodKey) {
            const v = this.valueOf(row, periodKey);
            if (v === null) return null;
            return row.neg ? -v : v;
        },
        fmtRow(row, v) {
            if (v === null || v === undefined) return '';
            if (row.field === '__rate_op') return (v * 100).toFixed(1) + '%';
            if (row.field === '__mkt_ratio') return v.toFixed(2) + '배';
            const shown = row.field === '__bep' ? v : (row.neg ? -Math.abs(v) : v);
            return Math.round(shown).toLocaleString();
        },
        cellText(row, ci) {
            const key = this.columns[ci].key;
            const v = this.displayValue(row, key);
            if (v === null) return '';
            if (row.field === '__rate_op') return (v * 100).toFixed(1) + '%';
            if (row.field === '__mkt_ratio') return v.toFixed(2) + '배';
            return Math.round(v).toLocaleString();
        },
        cellClass(row, ci) {
            const v = this.displayValue(row, this.columns[ci].key);
            return {
                neg: typeof v === 'number' && v < 0 && row.cls.indexOf('rate') === -1,
                'cell-link': row.cls.indexOf('rate') === -1 && row.field !== '__group'
            };
        },
        toggleGroup(key) {
            this.$set(this.openGroups, key, !this.openGroups[key]);
        },
        onCellClick(row, col) {
            if (row.cls.indexOf('rate') > -1 || row.field === '__group') return;
            this.$emit('period-click', { periodKey: col.key, unit: this.unit });
        }
    }
};
</script>

<style lang="scss" scoped>
.pnl-pivot {
    &__toolbar {
        display: flex;
        align-items: center;
        padding: 10px 14px;
        border-bottom: 1px solid #eef1f4;
    }
    &__label {
        font-weight: 600;
        margin-right: 10px;
    }
    &__scroll {
        overflow-x: auto;
    }
    &__table {
        border-collapse: collapse;
        width: 100%;
        white-space: nowrap;
        font-size: 13px;

        th, td {
            padding: 6px 10px;
            border-bottom: 1px solid #f0f3f6;
        }
        thead th {
            background: #f7f9fb;
            border-bottom: 2px solid #e1e6ea;
            font-weight: 600;
            color: #5a6b7b;
            text-align: right;
        }
        td {
            text-align: right;
            font-variant-numeric: tabular-nums;
        }
        th.acct {
            text-align: left;
            min-width: 190px;
            position: sticky;
            left: 0;
            background: #fff;
            z-index: 2;
        }
        thead th.acct {
            background: #f7f9fb;
            z-index: 3;
        }
        .col-total {
            background: #fafbfc;
            font-weight: 600;
            border-left: 2px solid #e1e6ea;
        }
        thead .col-total {
            background: #f2f5f8;
        }

        tr.lv0 > th.acct { font-weight: 700; }
        tr.lv0 td { font-weight: 600; }
        tr.detail > th.acct { padding-left: 26px; color: #7f8c9a; font-weight: 400; }
        tr.detail td { color: #7f8c9a; }
        tr.sum, tr.sum > th.acct { background: #f7f9fb; }
        tr.sep th, tr.sep td { border-top: 2px solid #dfe5ea; }
        tr.op, tr.op > th.acct { background: #fff8ec; }
        tr.op td, tr.op th { font-weight: 700; color: #b35309; }
        tr.rate td { color: #5a6b7b; font-weight: 600; }
        tr.warn > th.acct { color: #b35309; }
        tr.warn td { color: #b35309; }
    }

    .toggle {
        cursor: pointer;
        user-select: none;
        color: #2b6cb0;
        &::before { content: '▸ '; }
        &.open::before { content: '▾ '; }
    }
    .user-cat {
        font-size: 10px;
        color: #c3ccd4;
        margin-left: 4px;
    }
    .neg { color: #c0392b; }
    .cell-link { cursor: pointer; }
    .cell-link:hover { background: #eaf2fb; }
    .delta-up { color: #1e874b; }
    .delta-down { color: #c0392b; }
}
</style>
